<template>
  <div>
    <v-data-table
      id="objects_data_table"
      style="background: transparent;"
      ref="dataTable"
      :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
      :headers="headers"
      :hide-default-header="true"
      :loading="loading"
      :search="search"
      :items="listIDsObjects"
      :page="page"
      class="element"
      mobile-breakpoint=""
      @current-items="currentItems"
      @update:page="setPage"
    >
      <template #header="{ props }">
        <thead>
          <tr>
            <th v-for="(h, index) in props.headers" :key="index">
              <v-row no-gutters :justify="h.align">
                <v-tooltip top>
                  <template #activator="{ on }">
                    <span v-on="on">{{ h.text }}</span>
                  </template>
                  <span>{{ h.tooltip }}</span>
                </v-tooltip>
              </v-row>
            </th>
          </tr>
        </thead>
      </template>
      <template v-if="listIDsObjects.length" #body="{ items }">
        <tbody v-if="loading">
          <tr>
            <td :colspan="headers.length">
              <v-skeleton-loader
                class="mx-auto"
                type="table-tbody"
              />
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="item in items"
            :key="`${getKeyName(item)}/${getKeyPartition(item)}`"
            style="cursor: pointer"
            :class="selectedItem?.name === item.item ? 'highlight' : null"
            @click.stop="$emit('edit-obj', objects[value.indexOf(item.item)], page)"
          >
            <td
              :id="getKeyFQN(getKeyPartition(item), getKeyName(item))"
              class="ma-0 pa-0"
            >
              <v-list-item>
                <v-list-item-avatar v-if="objects[value.indexOf(item.item)]?.info">
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <svg
                        v-on="on"
                        style="width: 20px; height: 20px"
                        viewBox="0 0 24 24"
                      >
                        <path
                          :fill="
                            getKeyIconColor(
                              objects[value.indexOf(item.item)].info.type
                            )
                          "
                          :d="getKeyIcon(objects[value.indexOf(item.item)])"
                        />
                      </svg>
                    </template>
                    <span>{{
                      $tc(
                        getFriendlyObjType(
                          objects[value.indexOf(item.item)].info.type
                        )
                      )
                    }}</span>
                  </v-tooltip>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>{{ getKeyName(item) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </td>
            <template v-if="$vuetify.breakpoint.mdAndUp">
              <td>
                {{ getKeyPartition(item) }}
              </td>
              <td v-if="objects[value.indexOf(item.item)]?.data">
                {{ objects[value.indexOf(item.item)].data.name }}
              </td>
              <td v-else />
              <td v-if="objects[value.indexOf(item.item)]?.data">
                <span
                  :class="
                    getClassesForExpirationDate(
                      objects[value.indexOf(item.item)].data.notAfter
                    )
                  "
                >
                  {{
                    $getDateString({
                      date: objects[value.indexOf(item.item)].data.notAfter,
                      timeDetailLevel: 0,
                    })
                  }}
                </span>
              </td>
              <td v-else />
              <td v-if="objects[value.indexOf(item.item)]?.data">
                <template v-if="objects[value.indexOf(item.item)].data.linkedKey">
                  {{ objects[value.indexOf(item.item)].data.linkedKey }}
                </template>
              </td>
              <td v-else />
              <td v-if="objects[value.indexOf(item.item)]?.type">
                {{ objects[value.indexOf(item.item)].type.type }}
              </td>
              <td v-else />
              <td v-if="objects[value.indexOf(item.item)]?.type">
                {{ objects[value.indexOf(item.item)].type.size }}
              </td>
              <td v-else />
              <td v-if="objects[value.indexOf(item.item)]?.type">
                {{ objects[value.indexOf(item.item)].label }}
              </td>
              <td v-else />
              <td v-if="objects[value.indexOf(item.item)]?.lifecycle">
                {{ $t(objects[value.indexOf(item.item)].lifecycle) }}
              </td>
              <td v-else />
              <td v-if="isExportable({attr: objects[value.indexOf(item.item)]?.info.attr})">
                <v-icon>
                  check
                </v-icon>
              </td>
              <td v-else />

              <td v-if="hasBchainAttr({attr: objects[value.indexOf(item.item)]?.info.attr})">
                <v-icon>
                  check
                </v-icon>
              </td>
              <td v-else />
            </template>
            <td>
              <v-row no-gutters justify="end" v-if="objects[value.indexOf(item.item)]?.info">
                <menu-actions
                  :id="`menu_actions_${item.item}`"
                  :options="actionsList"
                  @open="optionsClick(objects[value.indexOf(item.item)], item, page)"
                  @close="closeMenu"
                />
              </v-row>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import MenuActions from '@/components/MenuActions.vue'
import PermissionsMixin from '@/mixins/permissions'
import ObjectUIMixin from '@/mixins/objectUI'

const ALG_OBJ_BLOB_X509 = 13

const MATCH_VALUE = 0
const RECOVER_VALUE = 1

export default {
  mixins: [PermissionsMixin, ObjectUIMixin],
  components: { MenuActions },
  data () {
    return {
      objects: [],
      value: null,
      selectedItem: null,
      showMenu: false,
      actionsList: [],
      defaultPage: 1,
      page: 1,
      loading: true,
      cspList: []
    }
  },
  props: {
    itemsPerPageOptions: {
      type: Array,
      default: () => [10, 20, 50, 100]
    },
    fullItems: {
      type: Array,
      required: true
    },
    search: {
      type: String,
      default: () => null
    }
  },
  async mounted () {
    document.addEventListener('keydown', this.scrollTable)

    const currentPage = parseInt(this.$route.query.p || 1)

    if (this.page !== currentPage) {
      setTimeout(() => {
        this.page = currentPage
        this.loading = false
      }, 500)
    } else {
      this.loading = false
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.scrollTable)
  },
  computed: {
    listIDsObjects () {
      return this.fullItems.map(item => ({ item }))
    },
    headers () {
      const mobileHeaders = [
        {
          text: this.$tc('name', 2),
          tooltip: this.$tc('name', 2),
          align: 'start',
          sortable: true,
          value: 'item'
        },
        {
          text: this.$tc('action', 2),
          tooltip: this.$tc('action', 2),
          align: 'end',
          sortable: false,
          value: null
        }
      ]
      const allHeaders = [
        {
          text: this.$tc('name', 2),
          tooltip: this.$tc('name', 2),
          align: 'center',
          sortable: true,
          value: 'item'
        },
        {
          text: this.$tc('partition'),
          tooltip: this.$tc('partition'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('common_name'),
          tooltip: this.$tc('common_name'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('not_valid_after'),
          tooltip: this.$tc('not_valid_after'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('key'),
          tooltip: this.$tc('key'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('type'),
          tooltip: this.$tc('type'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('size'),
          tooltip: this.$tc('size'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('label'),
          tooltip: this.$tc('label'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('lifecycle'),
          tooltip: this.$tc('lifecycle'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('exportable'),
          tooltip: this.$tc('exportable'),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('blockchain_module_compatible'),
          tooltip: this.$tc('blockchain_module_compatible', 2),
          align: 'start',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('action', 2),
          tooltip: this.$tc('action', 2),
          align: 'end',
          sortable: false,
          value: null,
          width: '150px'
        }
      ]
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm':
          return mobileHeaders
        case 'md':
        case 'lg':
        case 'xl':
          return allHeaders
        default:
          return allHeaders
      }
    }
  },
  methods: {
    setPage (page) {
      this.page = page

      this.$router.push({
        query: { p: page }
      })
    },
    scrollTable (event) {
      const tableWrapper = this.$refs.dataTable.$el.querySelector(
        '.v-data-table__wrapper'
      )
      if (event.key === 'ArrowLeft') {
        tableWrapper.scrollLeft -= 40
      } else if (event.key === 'ArrowRight') {
        tableWrapper.scrollLeft += 40
      }
    },
    async optionsClick (element, item, page) {
      this.selectedItem = element

      const hasBchainAttribute = this.hasBchainAttr({ attr: element.info.attr })

      this.actionsList = [
        {
          id: 'edit',
          condition: this.canCreateObjects,
          icon: 'edit',
          title: this.$tc('edit'),
          action: () => this.$emit('edit-obj', element, page)
        },
        {
          id: 'monitor',
          condition: true,
          icon: 'query_stats',
          title: this.$tc('monitor_object_btn'),
          action: () => this.redirectToMonitor(item.item)
        },
        {
          id: 'export',
          condition: !!(this.canCreateObjects && this.isExportable({ attr: element.info.attr })),
          icon: 'save_alt',
          title: this.$tc('export'),
          action: () => this.$emit('export', element, page)
        },
        {
          id: 'csr',
          condition: this.needsCSRP10(element.info.type),
          icon: 'mdi-file-certificate-outline',
          title: 'CSR/PKCS#10',
          action: () => this.$emit('csr', this.getKeyFQN(this.getKeyPartition(item), this.getKeyName(item)))
        },
        {
          id: 'get_pbk',
          condition: this.canGetPublicKey(element.type) && hasBchainAttribute,
          icon: 'mdi-script-text-key-outline',
          title: this.$tc('get_pbk'),
          action: () => this.$emit('get-pbk', element, page)
        },
        {
          id: 'derive',
          condition: this.canDerive(element.type) && hasBchainAttribute,
          icon: 'mdi-file-tree-outline',
          title: this.$tc('derive_key'),
          action: () => this.$emit('derive', element, page)
        },
        {
          id: 'address',
          condition: this.canGetAddress(element.type) && hasBchainAttribute,
          icon: 'mdi-key-link',
          title: this.$tc('get_addr'),
          action: () => this.$emit('get-addr', element, page)
        },
        {
          id: 'split_secret',
          condition: element.type.type === 'CSP',
          icon: 'share',
          title: this.$tc('split_secret'),
          action: () => this.$emit('split-secret', item.item, page)
        },
        {
          id: 'match',
          condition: element.type.type === 'CSP',
          icon: 'checklist_rtl',
          title: this.$tc('match'),
          action: () => this.$emit('match-secret', item.item, MATCH_VALUE, page)
        },
        {
          id: 'recover',
          condition: element.type.type === 'CSP',
          icon: 'rotate_left',
          title: this.$tc('recover'),
          action: () => this.$emit('recover-secret', item.item, RECOVER_VALUE, page)
        },
        {
          id: 'delete',
          condition: this.canDeleteObjects,
          icon: 'delete_outline',
          title: this.$tc('delete'),
          action: () => this.$emit('delete', this.getKeyFQN(this.getKeyPartition(item), this.getKeyName(item)), element)
        }
      ]
    },
    closeMenu () {
      this.selectedItem = null
    },
    async mountForList (items) {
      items = await Promise.all(
        items.map(async item => {
          if (item.name) {
            return item
          }

          const onlyName = this.getKeyName({ item })

          const blocked = await this.$store.dispatch('keys/objIsBlocked', {
            obj: item
          })

          const info = await this.$store.dispatch('keys/getKeyInfo', {
            obj: item
          })

          const sp80057 = await this.$store.dispatch('keys/getKeySP80057', {
            obj: item
          })

          const p11 = await this.$store.dispatch('keys/getP11Manager', {
            obj: item
          })

          const partition = this.getKeyPartition({ item })

          const lifecycle = this.$store.getters['keys/getSP80057State']({
            st: sp80057.state
          })

          const type = this.$store.getters['keys/getKeyType']({
            type: info.type
          })

          const label = p11.CKA_LABEL

          const fullItem = {
            name: onlyName,
            blocked,
            info,
            partition,
            lifecycle,
            type,
            label,
            data: null,
            cspInfo: null
          }

          if (info.type === ALG_OBJ_BLOB_X509) {
            const data = await this.getCertDetail(item)
            data.linkedKey = p11.HSM_ASSOCIATE
            fullItem.data = data
          }

          return fullItem
        })
      )

      return items
    },
    getKeyPartition ({ item }) {
      const a = item.split('/')
      if (a.length > 1) {
        return a[0]
      }
      return ''
    },
    getKeyName ({ item }) {
      const a = item.split('/')
      if (a.length > 1) {
        return a[1]
      }
      return item
    },
    getCertDetail (certName) {
      return this.$store.dispatch('certs/doParseCertificate', { certName })
    },
    getKeyFQN (partitionName, keyName) {
      let fqn = ''
      if (partitionName) fqn = partitionName + '/'
      return fqn.concat(keyName)
    },
    async currentItems (val) {
      const array = []
      this.objects = []
      val.forEach(item => {
        array.push(item.item)
      })
      this.value = array
      this.objects = await this.mountForList(array)
    },
    redirectToMonitor (id) {
      this.$router.push({ name: 'home', params: { objectId: id, selectedTab: 4 } })
    },
    canGetPublicKey (info) {
      const type = info.id
      switch (type) {
        case 'btcminikey':
        case 'bip32sxpriv':
        case 'bip39sxpriv':
        case 'bip32xpriv':
        case 'secp256k1':
        case 'secp256r1':
        case 'ed25519':
        case 'bip32ed25519xpriv':
          return true

        default:
          return false
      }
    },
    canDerive (info) {
      const type = info.id
      switch (type) {
        case 'btcminikey':
        case 'bip32sxpriv':
        case 'bip39sxpriv':
        case 'bip32xpriv':
          return true

        default:
          return false
      }
    },
    canGetAddress (info) {
      const type = info.id
      switch (type) {
        case 'btcminikey':
        case 'bip32sxpriv':
        case 'bip39sxpriv':
        case 'bip32xpriv':
        case 'secp256k1':
        case 'secp256r1':
        case 'ed25519':
        case 'bip32ed25519xpriv':
          return true

        default:
          return false
      }
    }
  },
  watch: { }
}
</script>

<style>
.element .v-data-table__wrapper::-webkit-scrollbar {
  height: 5px;
}
</style>
